<template>
    <div class="approval-card">
        <div class="panel-header">
            <div class="panel-title">工作日历</div>
            <div class="panel-extra">
                <div class="calendar-header-bar">
                    <a-space>
                        <a-button size="small" icon="left" shape="circle" @click="prevMonth" />
                        <span class="calendar-month-text">{{ currentMonth.format('YYYY年M月') }}</span>
                        <a-button size="small" icon="right" shape="circle" @click="nextMonth" />
                    </a-space>
                    <a-button size="small" type="primary" @click="openAddTodo">+ 待办</a-button>
                </div>
            </div>
        </div>
        <div class="panel-body calendar-wrap">
            <!-- AntD日历 -->
            <a-calendar
                v-model="currentMonth"
                :fullscreen="false"
                class="custom-calendar"
                @select="onSelectDate"
            >
                <template slot="dateCellRender" slot-scope="current">
                    <div class="cal-day-cell">
                        <div v-if="getMarkDay(current)" class="dot-group">
                            <span v-for="dot in getMarkDay(current)" :key="dot" class="cal-dot" :style="{background:dot}"></span>
                        </div>
                    </div>
                </template>
            </a-calendar>
            <!-- 图例 -->
            <div class="legend-row">
                <span><span class="dot blue"></span>会议</span>
                <span><span class="dot purple"></span>待办</span>
                <span><span class="dot orange"></span>出差</span>
                <span><span class="dot green"></span>个人日程</span>
            </div>

            <div class="divider-line"></div>

            <!-- 今日安排 -->
            <div class="today-plan">
                <div class="plan-title">
                    <span>今日安排 · {{ selectedMonthDay }}</span>
                </div>
                <div class="plan-list" v-if="planList.length > 0">
                    <div v-for="item in planList" :key="item.id" class="plan-item" :class="{'plan-item-disabled': !item.clickable}" @click="item.clickable && openDetail(item)">
                        <div class="plan-time">{{ item.time }}</div>
                        <div class="plan-line" :style="{background:item.lineColor}"></div>
                        <div class="plan-content">
                            <div class="plan-name">{{ item.name }}</div>
                            <div class="plan-desc">{{ item.remark || item.desc }}</div>
                        </div>
                    </div>
                </div>
                <div class="plan-empty" v-else>
                    <a-empty :image="simpleImage" description="暂无安排" />
                </div>
            </div>
        </div>
        <AddTodo ref="addTodoRef" @success="getCalendarData" />
    </div>
</template>

<script>
import moment from 'moment'
import { Empty } from 'ant-design-vue'
import { todoCalendar } from '@/api/approval/todo'
import AddTodo from './AddTodo.vue'

const TYPE_MAP = {
    meeting: { label: '会议', color: '#6490ff' },
    on_business_out: { label: '个人日程', color: '#18bc65' },
    on_business_in: { label: '出差', color: '#f59e0b' },
    todo_list: { label: '待办', color: '#9254de' }
}

export default {
    name: 'WorkCalendar',
    components: {
        AddTodo
    },
    data() {
        return {
            currentMonth: moment(),
            selectedDate: moment(),
            planList: [],
            markDays: {},
            monthList: [],
            simpleImage: Empty.PRESENTED_IMAGE_SIMPLE
        }
    },
    computed: {
        selectedMonthDay() {
            return this.selectedDate.format('M月D日')
        }
    },
    mounted() {
        this.getCalendarData()
    },
    methods: {
        getMarkDay(dayMoment) {
            const key = dayMoment.format('YYYY-MM-DD')
            return this.markDays[key] || null
        },
        openAddTodo() {
            this.$refs.addTodoRef.openModal(this.selectedDate)
        },
        openDetail(item) {
            const raw = this.monthList[item.id]
            if (raw) {
                this.$refs.addTodoRef.openDetail(raw, item.itemType)
            }
        },
        prevMonth() {
            this.currentMonth = this.currentMonth.clone().subtract(1, 'month')
            this.getCalendarData()
        },
        nextMonth() {
            this.currentMonth = this.currentMonth.clone().add(1, 'month')
            this.getCalendarData()
        },
        onSelectDate(date) {
            this.selectedDate = date.clone()
            this.updatePlanList()
        },
        getCalendarData() {
            const startDate = this.currentMonth.clone().startOf('month').format('YYYY-MM-DD')
            const endDate = this.currentMonth.clone().endOf('month').format('YYYY-MM-DD')
            console.log('[WorkCalendar] todoCalendar 参数:', { startDate, endDate })
            todoCalendar({ startDate, endDate }).then(res => {
                console.log('[WorkCalendar] todoCalendar 返回:', res)
                const list = Array.isArray(res) ? res : (res && res.list) || []
                this.monthList = list
                const markDays = {}
                list.forEach(item => {
                    const type = TYPE_MAP[item.type] || { label: item.type, color: '#9254de' }
                    const startDay = item.startTime ? item.startTime.slice(0, 10) : (item.date || '')
                    const endDay = item.endTime ? item.endTime.slice(0, 10) : startDay
                    const startMoment = moment(startDay)
                    const endMoment = moment(endDay)
                    for (let m = startMoment.clone(); m.isSameOrBefore(endMoment); m.add(1, 'day')) {
                        const key = m.format('YYYY-MM-DD')
                        if (!markDays[key]) markDays[key] = []
                        if (markDays[key].indexOf(type.color) === -1) {
                            markDays[key].push(type.color)
                        }
                    }
                })
                this.markDays = markDays
                this.updatePlanList()
            }).catch(err => {
                console.error('[WorkCalendar] todoCalendar 请求失败:', err)
            })
        },
        updatePlanList() {
            const today = this.selectedDate.format('YYYY-MM-DD')
            const todayMoment = moment(today)
            const planList = []
            const list = this.monthList || []
            list.forEach((item, index) => {
                const startDay = item.startTime ? item.startTime.slice(0, 10) : item.date
                const endDay = item.endTime ? item.endTime.slice(0, 10) : startDay
                const startMoment = moment(startDay)
                const endMoment = moment(endDay)
                if (todayMoment.isSameOrAfter(startMoment) && todayMoment.isSameOrBefore(endMoment)) {
                    const type = TYPE_MAP[item.type] || { label: item.type, color: '#9254de' }
                    const getTime = str => str ? str.slice(11, 16) : ''
                    const isMultiDay = startDay !== endDay
                    const timeRange = isMultiDay
                        ? (startDay.slice(5) + ' 至 ' + endDay.slice(5))
                        : (item.startTime && item.endTime
                            ? getTime(item.startTime) + ' 到 ' + getTime(item.endTime)
                            : (getTime(item.startTime) || ''))
                    planList.push({
                        id: index,
                        time: timeRange,
                        name: item.content,
                        remark: item.remark,
                        desc: type.label,
                        lineColor: type.color,
                        title: item.title,
                        itemType: item.type,
                        clickable: item.type === 'todo_list' || item.type === 'meeting'
                    })
                }
            })
            this.planList = planList
        }
    }
}
</script>

<style scoped>
.approval-card {
    background: #ffffff;
    border-radius: 12px;
    padding: 12px 20px 8px;
}
.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.panel-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    padding-left: 12px;
    /* border-left: 4px solid #2563eb; 删掉原来的border-left */
    position: relative;
}
.panel-title::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 16px; /* 控制竖线高度，按需修改 */
    background-color: #2563eb;
}

.text-link {
    color: #2563eb;
    cursor: pointer;
    text-decoration: none;
}
.text-link:hover {
    color: #1d4ed8;
}

/* ========== 日历自定义样式 ========== */
.calendar-header-bar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
}
.calendar-month-text {
    font-size: 14px;
    font-weight: 500;
}
::v-deep .custom-calendar .ant-fullcalendar-header {
    display: none !important;
}
::v-deep .custom-calendar .ant-fullcalendar-table th {
    font-weight: 500;
    color: #666;
    padding-bottom: 4px;
}
::v-deep .custom-calendar .ant-fullcalendar-date {
    height: 30px;
    background:#fbfbfb;
}
::v-deep .custom-calendar .ant-fullcalendar-selected-day .ant-fullcalendar-value {
    background-color: #2563eb !important;
    color: #fff !important;
    border-radius: 8px !important;
    width: 100%;
}
::v-deep .custom-calendar .ant-fullcalendar-value{
    width: 100% !important;
}
::v-deep .custom-calendar .ant-fullcalendar-value {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}
::v-deep .ant-fullcalendar{
    border-top: 0!important;
}
::v-deep .ant-fullcalendar table{
    height: 0px !important;
}
.cal-day-cell {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
.dot-group {
    display: flex;
    gap: 2px;
    margin-top: 3px;
}
.cal-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
}
.legend-row {
    display: flex;
    gap: 14px;
    margin-top: 2px;
    font-size: 12px;
    color: #666;
}
.dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;
}
.dot.blue { background: #4078f7; }
.dot.orange { background: #f59e0b; }
.dot.green { background: #18bc65; }
.dot.purple { background: #9254de; }
.divider-line {
    height: 1px;
    background: #eee;
    margin: 8px 0;
}
.today-plan {
}
.plan-title {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 6px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.plan-list {
    height: 100px;
    overflow-y: auto;
    /* 默认隐藏滚动条，鼠标悬停时显示；始终预留宽度避免悬停时内容跳动 */
    scrollbar-width: thin; /* Firefox */
    scrollbar-color: transparent transparent;
}
.plan-list::-webkit-scrollbar {
    width: 6px;
    background: transparent;
}
.plan-list::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
}
.plan-list::-webkit-scrollbar-track {
    background: transparent;
}
.plan-list:hover {
    scrollbar-color: #d9d9d9 transparent; /* Firefox */
}
.plan-list:hover::-webkit-scrollbar-thumb {
    background: #d9d9d9;
}
.plan-list:hover::-webkit-scrollbar-thumb:hover {
    background: #bfbfbf;
}
.plan-empty {
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.plan-empty ::v-deep .ant-empty-image {
    height: 22px;
    margin-bottom: 2px;
}
.plan-empty ::v-deep .ant-empty-image svg {
    height: 100%;
    width: auto;
}
.plan-empty ::v-deep .ant-empty-description {
    font-size: 12px;
    color: #999;
}
.plan-item {
    display: flex;
    gap: 10px;
    margin-bottom: 4px;
    padding: 2px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s;
}
.plan-item:hover {
    background-color: #f0f5ff;
}
.plan-item-disabled {
    cursor: not-allowed;
    opacity: 0.65;
}
.plan-item-disabled:hover {
    background-color: transparent;
}
.plan-line {
    width: 4px;
    border-radius: 3px;
    flex-shrink: 0;
}
.plan-time {
    width: 105px;
    font-size: 14px;
    font-weight: 600;
    color: #2563eb;
}
.plan-content {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}
.plan-name {
    font-size: 12px;
    font-weight: 600;
    margin: 1px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.plan-desc {
    font-size: 10px;
    color: #888;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

::v-deep .ant-fullcalendar-content{
    bottom: 3px!important;
}
::v-deep .ant-fullcalendar-calendar-body{
    padding: 8px 0px!important;
}
</style>
